/**
 * useTableUISettings Hook
 *
 * Reads table UI preferences from the uiSettings store and returns
 * computed Tailwind classes for consistent styling across all data tables.
 *
 * Usage:
 *   const { iconSize, badgeSize, rowPadding, colWidth } = useTableUISettings()
 */

import { useUISettings } from "@lib/stores/uiSettings";

type TableDensity = "compact" | "comfortable" | "wide";
type BadgeSize = "xs" | "sm" | "md";
type IconSize = "xs" | "sm" | "md";

// ──────────────────────── Class Maps ────────────────────────

const ICON_SIZE_MAP: Record<IconSize, string> = {
  xs: "w-2.5 h-2.5",
  sm: "w-3 h-3",
  md: "w-4 h-4",
};

const BADGE_SIZE_MAP: Record<BadgeSize, string> = {
  xs: "badge-xs",
  sm: "badge-sm",
  md: "badge-md",
};

const ROW_PADDING_MAP: Record<TableDensity, string> = {
  compact: "py-1 px-2",
  comfortable: "py-1.5 px-3",
  wide: "py-2.5 px-4",
};

const CELL_TEXT_MAP: Record<TableDensity, string> = {
  compact: "text-xs",
  comfortable: "text-sm",
  wide: "text-sm",
};

const COL_WIDTH_MAP: Record<
  TableDensity,
  { name: number; text: number; badge: number; actions: number }
> = {
  compact: { name: 160, text: 120, badge: 80, actions: 100 },
  comfortable: { name: 200, text: 180, badge: 110, actions: 130 },
  wide: { name: 260, text: 240, badge: 140, actions: 160 },
};

// ──────────────────────── Hook ────────────────────────

export interface TableUISettings {
  /** Current density preset */
  density: TableDensity;
  /** Current badge size preset */
  badgeSize: BadgeSize;
  /** Current icon size preset */
  iconSize: IconSize;
  /** Tailwind class for icon sizing (e.g. "w-3 h-3") */
  iconClass: string;
  /** Tailwind class for badge sizing (e.g. "badge-sm") */
  badgeClass: string;
  /** Tailwind class for row padding (e.g. "py-1.5 px-3") */
  rowPadding: string;
  /** Tailwind class for cell text size (e.g. "text-sm") */
  cellText: string;
  /** Pre-computed column widths by semantic role */
  colWidth: { name: number; text: number; badge: number; actions: number };
  /** Whether table animations are enabled */
  animation: boolean;
  /** Whether to show row numbers */
  showRowNumbers: boolean;
  /** Whether headers are sticky */
  stickyHeader: boolean;
}

export function useTableUISettings(): TableUISettings {
  const state = useUISettings() as { settings?: Record<string, any> };
  const settings = state?.settings;

  const density = (settings?.tables?.global?.density as TableDensity) ?? "comfortable";
  const badgeSize = (settings?.tables?.global?.badgeSize as BadgeSize) ?? "sm";
  const iconSize = (settings?.tables?.global?.iconSize as IconSize) ?? "sm";
  const animation = (settings?.tables?.global?.animation as boolean) ?? true;
  const showRowNumbers = (settings?.tables?.global?.showRowNumbers as boolean) ?? false;
  const stickyHeader = (settings?.tables?.global?.stickyHeader as boolean) ?? true;

  return {
    density,
    badgeSize,
    iconSize,
    iconClass: ICON_SIZE_MAP[iconSize],
    badgeClass: BADGE_SIZE_MAP[badgeSize],
    rowPadding: ROW_PADDING_MAP[density],
    cellText: CELL_TEXT_MAP[density],
    colWidth: COL_WIDTH_MAP[density],
    animation,
    showRowNumbers,
    stickyHeader,
  };
}
